import { useCallback, useEffect, useState } from 'react';
import { hrApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import type { HrWorkflowConfig } from '@/types/hr';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

const WORKFLOW_TYPES = [
  'LEAVE',
  'EXPENSE',
  'OVERTIME',
  'PAYROLL',
  'RESIGNATION',
  'RECRUITMENT',
  'ATTENDANCE',
  'PROMOTION',
  'TRANSFER',
];

const ROLE_OPTIONS = [
  { value: 'HR_MANAGE', label: 'HR Manager' },
  { value: 'HR_ADMIN', label: 'HR Admin' },
  { value: 'HR_ATTENDANCE_MANAGE', label: 'Attendance Manager' },
  { value: 'HR_PAYROLL_MANAGE', label: 'Payroll Manager' },
  { value: 'HR_RECRUITMENT_MANAGE', label: 'Recruitment Manager' },
  { value: 'HR_APPROVAL_MANAGE', label: 'Approval Manager' },
];

export function HrWorkflowsPage() {
  const [workflows, setWorkflows] = useState<HrWorkflowConfig[]>([]);
  const [selectedType, setSelectedType] = useState('LEAVE');
  const [workflowName, setWorkflowName] = useState('');
  const [stages, setStages] = useState<HrWorkflowConfig['stages']>([
    { stageOrder: 1, stageName: 'Primary Approval', approverRoleCode: 'HR_MANAGE', approvalMode: 'SEQUENTIAL' },
  ]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setWorkflows(await hrApi.listWorkflows());
    } catch (err) {
      showApiError(err, 'Failed to load workflows');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const existing = workflows.find((w) => w.workflowType === selectedType);
    if (existing) {
      setWorkflowName(existing.workflowName);
      setStages(existing.stages.length ? existing.stages : stages);
    } else {
      setWorkflowName(`${selectedType} Approval`);
      setStages([
        { stageOrder: 1, stageName: 'Primary Approval', approverRoleCode: 'HR_MANAGE', approvalMode: 'SEQUENTIAL' },
      ]);
    }
  }, [selectedType, workflows]);

  const addStage = () => {
    setStages((prev) => [
      ...prev,
      {
        stageOrder: prev.length + 1,
        stageName: `Stage ${prev.length + 1}`,
        approverRoleCode: 'HR_MANAGE',
        approvalMode: 'SEQUENTIAL',
      },
    ]);
  };

  const save = async () => {
    setSaving(true);
    try {
      await hrApi.saveWorkflow({
        workflowName,
        workflowType: selectedType,
        active: true,
        stages,
      });
      notify.success('Workflow saved');
      load();
    } catch (err) {
      showApiError(err, 'Failed to save workflow');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Approval workflows"
        description="Configure multi-stage approval chains — no hardcoded approvers"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'HR', to: '/hr' }, { label: 'Workflows' }]}
      />
      <div className="grid gap-6 lg:grid-cols-3">
        <section className="card p-4 lg:col-span-1">
          <h3 className="mb-3 font-semibold">Workflow types</h3>
          {loading && <p className="text-sm text-slate-500">Loading…</p>}
          <ul className="space-y-1">
            {WORKFLOW_TYPES.map((type) => {
              const configured = workflows.some((w) => w.workflowType === type);
              return (
                <li key={type}>
                  <button
                    type="button"
                    className={`w-full rounded px-3 py-2 text-left text-sm ${
                      selectedType === type ? 'bg-primary-100 font-medium dark:bg-primary-900/40' : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                    }`}
                    onClick={() => setSelectedType(type)}
                  >
                    {type}
                    {configured && <span className="ml-2 text-xs text-green-600">configured</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
        <section className="card space-y-4 p-4 lg:col-span-2">
          <Input label="Workflow name" value={workflowName} onChange={(e) => setWorkflowName(e.target.value)} />
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Approval stages</h3>
            <Button variant="secondary" onClick={addStage}>
              Add stage
            </Button>
          </div>
          {stages.map((stage, idx) => (
            <div key={idx} className="grid gap-2 rounded border border-slate-200 p-3 dark:border-slate-700 md:grid-cols-3">
              <Input
                label="Stage name"
                value={stage.stageName ?? ''}
                onChange={(e) =>
                  setStages((prev) =>
                    prev.map((s, i) => (i === idx ? { ...s, stageName: e.target.value } : s)),
                  )
                }
              />
              <Select
                label="Approver role"
                value={stage.approverRoleCode ?? 'HR_MANAGE'}
                onChange={(v) =>
                  setStages((prev) =>
                    prev.map((s, i) => (i === idx ? { ...s, approverRoleCode: v } : s)),
                  )
                }
                options={ROLE_OPTIONS}
              />
              <Select
                label="Mode"
                value={stage.approvalMode ?? 'SEQUENTIAL'}
                onChange={(v) =>
                  setStages((prev) => prev.map((s, i) => (i === idx ? { ...s, approvalMode: v } : s)))
                }
                options={[
                  { value: 'SEQUENTIAL', label: 'Sequential' },
                  { value: 'PARALLEL', label: 'Parallel' },
                ]}
              />
            </div>
          ))}
          <Button loading={saving} onClick={() => void save()}>
            Save workflow
          </Button>
        </section>
      </div>
    </div>
  );
}
